import { NgTemplateOutlet } from '@angular/common';
import { Component, ChangeDetectionStrategy, computed, inject, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PeopleService, PersonSearchResult } from '../../../core/people/people';

/** Names per lookup request (F-027 follow-up: progress feedback). The
 *  endpoint itself accepts up to MAX_BULK_NAMES in one call, but a single
 *  big request gives no visibility into progress until it all comes back
 *  — splitting client-side into smaller sequential requests lets the UI
 *  show a live "X of Y" count instead of an opaque wait. */
const LOOKUP_CHUNK_SIZE = 5;

type RowStatus = 'collapsed' | 'expanded' | 'adding' | 'added' | 'error';

interface RowViewModel {
  name: string;
  /** Wikipedia hits with a confirmed birth date — real person matches. */
  visible: PersonSearchResult[];
  /** Non-person noise (film pages, discographies, etc. — no confirmed
   *  birth date), same "hidden by default" idiom as the single-search
   *  panel in admin-people-list. Still pickable via "Show Hidden". */
  hidden: PersonSearchResult[];
  status: RowStatus;
  errorMessage?: string;
  showHidden: boolean;
}

/**
 * Bulk version of the single-name "Add a person" search (F-027) — paste a
 * comma-separated list, look each one up, and only show what's missing
 * from the catalog. Built for recreating a whole year's real entries at
 * once rather than one-at-a-time.
 *
 * Single step transition: "input" (paste names, look up) -> "results"
 * (one row per not-found name, with an Add action). No "back" — closing
 * and reopening starts a fresh lookup, same as PersonModal's one-shot
 * lifecycle.
 *
 * A row's Add button behavior depends on its *visible* (real person)
 * candidate count, not the raw count the API returns — Wikipedia search
 * results include non-person noise (a celebrity's movies, discography,
 * etc.) with no confirmed birth date, exactly what admin-people-list's
 * existing single-search panel already filters via addVisibleResults/
 * addHiddenResults. Without the same filter here, a clean single real
 * match (e.g. "Freddie Mercury", which also returns "Bohemian Rhapsody
 * (film)" and "Freddie Mercury discography") would wrongly look
 * ambiguous — confirmed live against the real API during F-027.
 */
@Component({
  selector: 'app-bulk-add-people-modal',
  imports: [ReactiveFormsModule, NgTemplateOutlet],
  templateUrl: './bulk-add-people-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bulk-add-people-modal.css',
})
export class BulkAddPeopleModal {
  private readonly people = inject(PeopleService);

  readonly closed = output<void>();
  /** Emitted after each successful add, so the parent list refreshes. */
  readonly changed = output<void>();

  readonly step = signal<'input' | 'results'>('input');

  readonly namesForm = new FormGroup({
    names: new FormControl('', { nonNullable: true }),
  });
  readonly lookingUp = signal(false);
  readonly lookupError = signal<string | null>(null);
  /** Names resolved so far / total, while lookingUp — drives the progress
   *  bar and "X of Y" counter. */
  readonly processedCount = signal(0);
  readonly totalToProcess = signal(0);
  readonly progressPercent = computed(() =>
    this.totalToProcess() === 0
      ? 0
      : Math.round((this.processedCount() / this.totalToProcess()) * 100),
  );

  readonly rows = signal<RowViewModel[]>([]);
  /** For the "N of M already in the catalog" summary line. */
  readonly inputCount = signal(0);
  readonly alreadyCatalogedCount = computed(() => this.inputCount() - this.rows().length);

  close(): void {
    this.closed.emit();
  }

  async lookUp(): Promise<void> {
    const names = parseNames(this.namesForm.getRawValue().names);
    if (names.length === 0) return;

    this.lookingUp.set(true);
    this.lookupError.set(null);
    this.processedCount.set(0);
    this.totalToProcess.set(names.length);
    try {
      const allResults: { name: string; candidates: PersonSearchResult[] }[] = [];
      for (let i = 0; i < names.length; i += LOOKUP_CHUNK_SIZE) {
        const chunk = names.slice(i, i + LOOKUP_CHUNK_SIZE);
        const chunkResults = await this.people.bulkLookup(chunk);
        allResults.push(...chunkResults);
        this.processedCount.update((n) => n + chunk.length);
      }
      this.inputCount.set(names.length);
      this.rows.set(allResults.map(toRowViewModel));
      this.step.set('results');
    } catch {
      this.lookupError.set('Lookup failed — try again in a moment.');
    } finally {
      this.lookingUp.set(false);
    }
  }

  /** Single visible candidate -> add it directly. Multiple -> reveal the
   *  picker instead of guessing. Zero visible (but some hidden) -> reveal
   *  the picker too, since the "no confirmed birth date" ones are still
   *  real Wikipedia pages that occasionally are the right person. */
  onAddClick(row: RowViewModel): void {
    if (row.visible.length === 1) {
      this.addCandidate(row, row.visible[0]);
    } else if (row.visible.length > 0 || row.hidden.length > 0) {
      this.updateRow(row.name, { status: 'expanded' });
    }
    // Zero candidates entirely: nothing to do — the template also disables
    // the button in this case, this guard just keeps the method itself
    // correct regardless of caller.
  }

  async pickCandidate(row: RowViewModel, candidate: PersonSearchResult): Promise<void> {
    await this.addCandidate(row, candidate);
  }

  collapseRow(row: RowViewModel): void {
    this.updateRow(row.name, { status: 'collapsed' });
  }

  toggleHidden(row: RowViewModel): void {
    this.updateRow(row.name, { showHidden: !row.showHidden });
  }

  candidateKey(candidate: PersonSearchResult): string {
    return candidate.wikipediaUrl ?? candidate.name;
  }

  private async addCandidate(row: RowViewModel, candidate: PersonSearchResult): Promise<void> {
    this.updateRow(row.name, { status: 'adding' });
    try {
      await this.people.select({
        name: candidate.name,
        knownFor: candidate.knownFor ?? undefined,
        dateOfBirth: candidate.dateOfBirth ?? undefined,
        dateOfDeath: candidate.dateOfDeath ?? undefined,
        causeOfDeath: candidate.causeOfDeath ?? undefined,
        wikipediaUrl: candidate.wikipediaUrl ?? undefined,
        imageUrl: candidate.imageUrl ?? undefined,
        aliases: candidate.aliases,
      });
      this.updateRow(row.name, { status: 'added' });
      this.changed.emit();
    } catch {
      this.updateRow(row.name, {
        status: 'error',
        errorMessage: 'Failed to add — try again.',
      });
    }
  }

  private updateRow(name: string, patch: Partial<RowViewModel>): void {
    this.rows.update((rows) =>
      rows.map((r) => (r.name === name ? { ...r, ...patch } : r)),
    );
  }
}

/** Splits on commas, trims, drops empties, dedupes case-insensitively —
 *  mirrors the backend's own dedupe so the "N of M already catalogued"
 *  summary line matches what was actually sent. */
function parseNames(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const name = part.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

function toRowViewModel(result: { name: string; candidates: PersonSearchResult[] }): RowViewModel {
  return {
    name: result.name,
    visible: result.candidates.filter((c) => c.dateOfBirth !== null),
    hidden: result.candidates.filter((c) => c.dateOfBirth === null),
    status: 'collapsed',
    showHidden: false,
  };
}
